// Static category and location options for proposal creation.
// These are used by the create-proposal form instead of fetching from API routes.

import { COUNTRIES, countryCodeToFlag } from '@/config/countries';

export interface CategoryOption {
  id: string;
  name: string;
  emoji: string;
}

export interface LocationOption {
  id: string;
  name: string;
  emoji: string;
}

// Opinion-market discovery and creation order.
export const PROPOSAL_CATEGORIES: CategoryOption[] = [
  { id: 'culture', name: 'Culture', emoji: '\u{1F3AD}' },
  { id: 'sports', name: 'Sports', emoji: '\u{26BD}' },
  { id: 'gaming', name: 'Gaming', emoji: '\u{1F3AE}' },
  { id: 'crypto', name: 'Crypto', emoji: '\u{1FA99}' },
  { id: 'tech', name: 'Tech', emoji: '\u{1F4BB}' },
  { id: 'politics', name: 'Politics', emoji: '\u{1F3DB}\u{FE0F}' },
  { id: 'business', name: 'Business', emoji: '\u{1F4BC}' },
  { id: 'lifestyle', name: 'Lifestyle', emoji: '\u{1F33F}' },
];

export const DISCOVERY_CATEGORIES: readonly CategoryOption[] = PROPOSAL_CATEGORIES;

// Legacy categories remain in browser-saved drafts and onboarding progress.
const LEGACY_CATEGORY_IDS: ReadonlyMap<string, string> = new Map([
  ['entertainment', 'culture'],
  ['music', 'culture'],
  ['memes', 'culture'],
  ['technology', 'tech'],
  ['science', 'tech'],
  ['finance', 'business'],
  ['economics', 'business'],
  ['law', 'politics'],
  ['world', 'politics'],
  ['environment', 'politics'],
  ['food', 'lifestyle'],
  ['health', 'lifestyle'],
]);

// Short names for locations that existed before the full ISO list; saved
// filters and URLs match locations by display name.
const LOCATION_NAME_OVERRIDES: Readonly<Record<string, string>> = {
  kr: 'South Korea',
  ru: 'Russia',
  us: 'United States',
  vn: 'Vietnam',
};

// `global` plus every country a user can set on their profile.
export const PROPOSAL_LOCATIONS: LocationOption[] = [
  { id: 'global', name: 'Global', emoji: '\u{1F310}' },
  ...COUNTRIES.map((country): LocationOption => {
    const id = country.code.toLowerCase();
    return {
      id,
      name: LOCATION_NAME_OVERRIDES[id] ?? country.name,
      emoji: countryCodeToFlag(country.code),
    };
  }),
];

/** Map location id or display name -> flag emoji for use in list/detail. */
const LOCATION_FLAG_BY_ID = Object.fromEntries(
  PROPOSAL_LOCATIONS.map((loc) => [loc.id.toLowerCase(), loc.emoji]),
) as Record<string, string>;
const LOCATION_FLAG_BY_NAME = Object.fromEntries(
  PROPOSAL_LOCATIONS.map((loc) => [loc.name.toLowerCase(), loc.emoji]),
) as Record<string, string>;

/** Display strings that appear in fixtures or API but are not our canonical id/name. */
const LOCATION_FLAG_OVERRIDES: Record<string, string> = {
  'london, uk': PROPOSAL_LOCATIONS.find((l) => l.id === 'gb')?.emoji ?? '\u{1F1EC}\u{1F1E7}',
};

/**
 * Returns the flag emoji for a location string (id, label, or fixture display value), or null.
 */
export function getLocationFlag(location: string): string | null {
  const trimmed = location.trim();
  if (trimmed.length === 0) return null;
  const key = trimmed.toLowerCase();
  return (
    LOCATION_FLAG_BY_ID[key] ?? LOCATION_FLAG_BY_NAME[key] ?? LOCATION_FLAG_OVERRIDES[key] ?? null
  );
}

/**
 * Convert a category value (display name OR id) to the canonical lowercase id
 * the backend AI routes expect (`z.enum(CATEGORIES)` of slug ids). Browser-saved
 * forms can also retain retired category values; migrate those at the boundary.
 */
export function categoryNameToId(value: string): string {
  const v = value.trim();
  if (v.length === 0) return v;
  const byId = PROPOSAL_CATEGORIES.find((c) => c.id.toLowerCase() === v.toLowerCase());
  if (byId !== undefined) return byId.id;
  const byName = PROPOSAL_CATEGORIES.find((c) => c.name.toLowerCase() === v.toLowerCase());
  return byName?.id ?? LEGACY_CATEGORY_IDS.get(v.toLowerCase()) ?? v.toLowerCase();
}

/**
 * Convert a location value (display name OR id) to the canonical lowercase id
 * the backend stores on `market.location` / `proposal.location`. The region
 * multi-select holds the display name (`"United States"`), but the indexer
 * stores the slug (`"us"`) — call this at the wire boundary before filtering,
 * mirroring {@link categoryNameToId}.
 */
export function locationNameToId(value: string): string {
  const v = value.trim();
  if (v.length === 0) return v;
  const byId = PROPOSAL_LOCATIONS.find((l) => l.id.toLowerCase() === v.toLowerCase());
  if (byId !== undefined) return byId.id;
  const byName = PROPOSAL_LOCATIONS.find((l) => l.name.toLowerCase() === v.toLowerCase());
  return byName?.id ?? v.toLowerCase();
}
